import { useState } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    }, 1500);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      <div className="pt-24 pb-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-light text-gray-900 mb-6 tracking-wide">
              Get in Touch
            </h1>
            <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Have questions about Kent's work, interested in purchasing artwork, or looking to collaborate? Reach out and start a conversation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-light text-gray-900 mb-6 tracking-wide">Send a Message</h2>
              
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">Thank you for your message! We'll get back to you soon.</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                    Subject
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm cursor-pointer"
                  >
                    <option value="">Select a subject</option>
                    <option value="general">General Inquiry</option>
                    <option value="artwork">Artwork Purchase</option>
                    <option value="collaboration">Collaboration</option>
                    <option value="press">Press & Media</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm resize-none"
                    placeholder="Your message (max 500 characters)"
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-8 py-4 bg-amber-700 text-white text-sm font-light tracking-widest uppercase hover:bg-amber-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <div className="bg-stone-50 rounded-lg p-8 mb-8">
                <h2 className="text-2xl font-light text-gray-900 mb-6 tracking-wide">Contact Information</h2>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                      <i className="ri-mail-line text-xl text-amber-700"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 mb-1">Email</h3>
                      <a href="mailto:kzimmerman@wilhoitproperties.com" className="text-sm text-gray-600 hover:text-amber-700 transition-colors cursor-pointer">
                        kzimmerman@wilhoitproperties.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                      <i className="ri-time-line text-xl text-amber-700"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 mb-1">Response Time</h3>
                      <p className="text-sm text-gray-600">
                        Typically within 1-2 business days
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                      <i className="ri-book-open-line text-xl text-amber-700"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 mb-1">For Book Orders</h3>
                      <p className="text-sm text-gray-600">
                        Available on Amazon and select bookstores
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-8">
                <h2 className="text-2xl font-light text-gray-900 mb-6 tracking-wide">Connect on Social Media</h2>
                <div className="grid grid-cols-2 gap-4">
                  <a 
                    href="https://www.facebook.com/people/Kent-Zimmerman-Author-Artist/61584956443998/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-3 px-6 py-4 border border-gray-300 rounded-lg hover:border-amber-700 hover:bg-amber-50 transition-all cursor-pointer"
                  >
                    <i className="ri-facebook-fill text-2xl text-gray-700"></i>
                    <span className="text-sm font-light text-gray-700">Facebook</span>
                  </a>
                  <a 
                    href="https://www.threads.com/@theartofkent?xmt=AQF0XocT62Ll8Tku3CgQk8lpdO-Bj47sscvDVhMkCA8qDPg"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-3 px-6 py-4 border border-gray-300 rounded-lg hover:border-amber-700 hover:bg-amber-50 transition-all cursor-pointer"
                  >
                    <i className="ri-at-line text-2xl text-gray-700"></i>
                    <span className="text-sm font-light text-gray-700">Threads</span>
                  </a>
                  <a 
                    href="https://www.instagram.com/theartofkent/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-3 px-6 py-4 border border-gray-300 rounded-lg hover:border-amber-700 hover:bg-amber-50 transition-all cursor-pointer"
                  >
                    <i className="ri-instagram-fill text-2xl text-gray-700"></i>
                    <span className="text-sm font-light text-gray-700">Instagram</span>
                  </a>
                  <a 
                    href="https://www.linkedin.com/in/kent-zimmerman-4059411a1?trk=people-guest_people_search-card&original_referer=https%3A%2F%2Fwww.linkedin.com%2Fpub%2Fdir%2FKent%2FZimmerman"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-3 px-6 py-4 border border-gray-300 rounded-lg hover:border-amber-700 hover:bg-amber-50 transition-all cursor-pointer"
                  >
                    <i className="ri-linkedin-fill text-2xl text-gray-700"></i>
                    <span className="text-sm font-light text-gray-700">LinkedIn</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
